import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { SectionIndices } from '../../../common/i-doc/i-doc.registry.ts';
import { collaborationPlan as c } from '@/styles/tokens';
import { CollaborationStep } from '@/services/collaboration-plan.service';
import { CollaborationPlanRow } from './CollaborationPlanRow';
import { CollaborationPlanAllTasksPanel } from './CollaborationPlanAllTasksPanel';
import { CollaborationStepFormDialog } from './CollaborationStepFormDialog';
import { CollaborationPlanPresetDialog } from './CollaborationPlanPresetDialog';

interface CollaborationPlanCardProps {
  items: CollaborationStep[];
  /** Реальна кількість активних задач (без DASHBOARD_LIMIT, бек: `getActivePlan` → `meta.total`) -
   *  для футера "Переглянути всі задачі (N)". Раніше рахували з `items.length` (капований список) -
   *  при >4 активних задачах N було заниженим. */
  totalCount: number;
  clientId: number | string;
  clientName: string;
  onComplete: (stepId: number) => void;
  onDelete: (stepId: number) => void;
  onChanged: () => void;
}

/** Collaboration plan (Figma, 2026-10-29): іконка-бейдж за типом задачі, дата+час, олівець
 *  (редагування) + кошик + стрілка "перейти до дії" (видима лише коли бек віддав `actionIntent`
 *  для кроку - `CollaborationPlanRow`/`collaborationStepIcons.tsx`/`collaborationStepActions.ts`).
 *  "+ Додати" й олівець відкривають `CollaborationStepFormDialog` (та сама форма для додавання й
 *  редагування). "Переглянути всі задачі" відкриває `CollaborationPlanAllTasksPanel` (бокова панель). */
export const CollaborationPlanCard: React.FC<CollaborationPlanCardProps> = ({
  items, totalCount, clientId, clientName, onComplete, onDelete, onChanged,
}) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'clientProfile.collaborationPlan' });
  const [panelOpen, setPanelOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [presetOpen, setPresetOpen] = useState(false);
  const [editingStep, setEditingStep] = useState<CollaborationStep | null>(null);

  const handleAddClick = () => { setEditingStep(null); setFormOpen(true); };
  const handleEditStep = (stepId: number) => {
    const step = items.find((i) => i.id === stepId);
    if (step) { setEditingStep(step); setFormOpen(true); }
  };

  return (
    <Box
      data-i-doc={SectionIndices.EXPERT_CLIENT_COLLABORATION_PLAN}
      sx={{ bgcolor: c.background, boxShadow: c.shadow, borderRadius: '12px', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, pt: 2, pb: 1 }}>
        <Typography sx={{ fontSize: 16, lineHeight: '19px', fontWeight: 500, color: c.title }}>{t('title')}</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            onClick={() => setPresetOpen(true)}
            sx={{
              border: `1px solid ${c.addButtonText}`, color: c.addButtonText, borderRadius: '6px', px: 2, py: '6px',
              fontSize: 12, fontWeight: 500, textTransform: 'none',
              '&:hover': { bgcolor: c.addButtonBg },
            }}
          >
            {t('preset.button')}
          </Button>
          <Button
            size="small"
            startIcon={<AddIcon sx={{ fontSize: 16 }} />}
            onClick={handleAddClick}
            sx={{
              bgcolor: c.addButtonBg, color: c.addButtonText, borderRadius: '6px', px: 2, py: '6px',
              fontSize: 12, fontWeight: 500, textTransform: 'none', boxShadow: 'none',
              '&:hover': { bgcolor: c.addButtonBg, boxShadow: 'none' },
            }}
          >
            {t('add')}
          </Button>
        </Box>
      </Box>

      <Box sx={{ px: 2 }}>
        {items.map((item, index) => (
          <CollaborationPlanRow
            key={item.id}
            item={item}
            isLast={index === items.length - 1}
            clientName={clientName}
            onComplete={onComplete}
            onDelete={onDelete}
            onEdit={handleEditStep}
          />
        ))}
      </Box>

      <Box sx={{ bgcolor: c.footerBg, py: '6px', display: 'flex', justifyContent: 'center' }}>
        <Button
          size="small"
          endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
          onClick={() => setPanelOpen(true)}
          sx={{ color: c.footerText, textTransform: 'none', fontSize: 14, fontWeight: 500 }}
        >
          {t('viewAll', { count: totalCount })}
        </Button>
      </Box>

      <CollaborationPlanAllTasksPanel
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        clientId={clientId}
        items={items}
        clientName={clientName}
        onComplete={onComplete}
        onDelete={onDelete}
        onEdit={handleEditStep}
        onAdd={handleAddClick}
      />

      <CollaborationStepFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        clientId={clientId}
        editingStep={editingStep}
        onSaved={onChanged}
      />

      <CollaborationPlanPresetDialog
        open={presetOpen}
        onClose={() => setPresetOpen(false)}
        clientId={clientId}
        onSaved={onChanged}
      />
    </Box>
  );
};
